import { AmountSuperScript } from "components/design/AmountSuperScript";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { CornerDownRight } from "components/icons/CornerDownRight";
import { InternalTransaction } from "components/Transaction/AddTransaction/AddTransactionManuallyModal";
import { useFormikContext } from "formik";
import { FormatOptionLabelMeta } from "react-select";
import { VendorBill } from "types/Models/vendorBills";

export const BillAllocationRow = ({
  rowIndex,
  selectedBills,
}: {
  rowIndex: number;
  selectedBills: VendorBill[];
}) => {
  const { values } = useFormikContext<{
    transactions: InternalTransaction[];
  }>();

  const billIdsAssignedElsewhere = new Set(
    values.transactions
      .filter((_transaction, index) => index !== rowIndex)
      .map((transaction) => transaction.withheldBillId)
      .filter((billId): billId is string => Boolean(billId))
  );
  const availableBills = selectedBills.filter(
    (bill) => !billIdsAssignedElsewhere.has(bill.uuid)
  );

  const options = availableBills.map((bill) => ({
    label: bill.bill_number,
    value: bill.uuid,
  }));

  // The closed value shows just the bill number (matches the design); the
  // open list additionally shows vendor + bill amount per option.
  const formatOptionLabel = (
    option: OptionData,
    meta: FormatOptionLabelMeta<OptionData>
  ) => {
    if (meta.context === "value") {
      return option.label;
    }

    const bill = availableBills.find(({ uuid }) => uuid === option.value);
    if (!bill) return option.label;

    return (
      <div className="t-min-w-0 t-w-full">
        <div className="t-truncate t-text-body-sm t-text-text-60">
          {bill.bill_number}
        </div>
        <div className="t-flex t-items-center t-text-caption t-text-text-30">
          <span className="t-min-w-0 t-truncate">
            {bill.entity_merchant_data?.name || "-"}
          </span>
          <span className="t-shrink-0 t-whitespace-nowrap">
            &nbsp;| Bill amount:{" "}
            <AmountSuperScript amount={Number(bill.due_balance)} />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="t-pb-2.5">
      <div className="t-relative t-pl-12">
        <span className="t-absolute t-left-4 t-top-0 t-bottom-1/2 t-flex t-text-neutral-40">
          <CornerDownRight className="t-h-full t-w-auto" strokeWidth={1.5} />
        </span>
        <div className="t-flex t-items-center t-gap-5">
          <p className="t-text-body-sm-lite t-text-text-30 t-text-right t-m-0 t-whitespace-nowrap">
            Difference resolved as Withheld Tax Payable, owed to the IRS. Select
            the applicable bill:
          </p>
          <div className="t-w-40">
            <Combobox
              withForm
              size="small"
              name={`transactions[${rowIndex}].withheldBillId`}
              menuPortalTarget={document.body}
              placeholder="Select bill"
              classNames={{ menu: () => "t-min-w-64" }}
              options={options}
              formatOptionLabel={formatOptionLabel}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
